// Brand Identity Showcase Component
// Visual demonstration of the MUU Agricultural Essence brand identity using centralized design tokens.

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  Chip,
  Paper,
  Stack,
  Divider,
  Grid,
} from '@mui/material';

import { DESIGN_TOKENS } from '../tokens/colors';
import { TYPOGRAPHY_TOKENS, getTextStyle } from '../tokens/typography';

const ColorSwatch: React.FC<{ color: string; name: string; hex: string }> = ({ color, name, hex }) => (
  <Box sx={{ textAlign: 'center', minWidth: 100 }}>
    <Box
      sx={{
        width: 80,
        height: 80,
        backgroundColor: color,
        borderRadius: 2,
        mb: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    />
    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
      {name}
    </Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
      {hex}
    </Typography>
  </Box>
);

const TypographyShowcase: React.FC = () => (
  <Stack spacing={3}>
    <Typography sx={{ ...getTextStyle('h1'), color: DESIGN_TOKENS.colors.text.primary }}>
      Agricultural Intelligence
    </Typography>
    
    <Typography sx={{ ...getTextStyle('h2'), color: DESIGN_TOKENS.colors.brand.forest }}>
      Smart Farming Solutions
    </Typography>
    
    <Typography sx={{ ...getTextStyle('bodyLarge'), color: DESIGN_TOKENS.colors.text.secondary, maxWidth: '600px' }}>
      MUU combines cutting-edge artificial intelligence with deep agricultural expertise 
      to deliver sustainable, data-driven solutions for modern farming operations.
    </Typography>
    
    <Typography sx={{ ...getTextStyle('body1'), color: DESIGN_TOKENS.colors.text.tertiary, maxWidth: '500px' }}>
      Our platform integrates seamlessly with existing farm management systems, 
      providing real-time insights and predictive analytics.
    </Typography>
  </Stack>
);

const ComponentShowcase: React.FC = () => (
  <Grid container spacing={3}>
    {/* Primary Action Button */}
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ fontSize: 48, color: DESIGN_TOKENS.colors.brand.green, mb: 2 }}>🌱</Box>
        <Typography sx={{ ...getTextStyle('h6'), mb: 2 }}>
          Farm Management
        </Typography>
        <Button
          variant="contained"
          sx={{
            ...getTextStyle('button'),
            backgroundColor: DESIGN_TOKENS.colors.brand.green,
            color: DESIGN_TOKENS.colors.text.inverse,
            '&:hover': {
              backgroundColor: DESIGN_TOKENS.colors.brand.forest,
            },
            borderRadius: 2,
            px: 3,
            py: 1.5,
          }}
        >
          Get Started
        </Button>
      </Card>
    </Grid>

    {/* Secondary Action */}
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ fontSize: 48, color: DESIGN_TOKENS.colors.brand.sage, mb: 2 }}>📈</Box>
        <Typography sx={{ ...getTextStyle('h6'), mb: 2 }}>
          Analytics Dashboard
        </Typography>
        <Button
          variant="outlined"
          sx={{
            ...getTextStyle('button'),
            borderColor: DESIGN_TOKENS.colors.brand.sage,
            color: DESIGN_TOKENS.colors.brand.sage,
            '&:hover': {
              backgroundColor: `${DESIGN_TOKENS.colors.brand.sage}15`,
              borderColor: DESIGN_TOKENS.colors.brand.sage,
            },
            borderRadius: 2,
            px: 3,
            py: 1.5,
          }}
        >
          View Insights
        </Button>
      </Card>
    </Grid>

    {/* Success State */}
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ fontSize: 48, color: DESIGN_TOKENS.colors.status.success[500], mb: 2 }}>🌿</Box>
        <Typography sx={{ ...getTextStyle('h6'), mb: 2 }}>
          Sustainability
        </Typography>
        <Chip
          label="Carbon Neutral"
          sx={{
            backgroundColor: DESIGN_TOKENS.colors.status.success[50],
            color: DESIGN_TOKENS.colors.status.success[600],
            ...getTextStyle('caption'),
            fontWeight: TYPOGRAPHY_TOKENS.fontWeights.bold,
          }}
        />
      </Card>
    </Grid>
  </Grid>
);

const DataVisualization: React.FC = () => (
  <Paper sx={{ p: 3, backgroundColor: DESIGN_TOKENS.colors.background.elevated }}>
    <Typography sx={{ ...getTextStyle('h5'), color: DESIGN_TOKENS.colors.text.primary, mb: 3 }}>
      Farm Performance Metrics
    </Typography>
    
    <Grid container spacing={3}>
      <Grid size={{ xs: 6, md: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ ...getTextStyle('h3'), color: DESIGN_TOKENS.colors.primary[500] }}>
            94%
          </Typography>
          <Typography sx={{ ...getTextStyle('body2'), color: DESIGN_TOKENS.colors.text.secondary }}>
            Crop Yield
          </Typography>
        </Box>
      </Grid>
      
      <Grid size={{ xs: 6, md: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ ...getTextStyle('h3'), color: DESIGN_TOKENS.colors.secondary[500] }}>
            2.3M
          </Typography>
          <Typography sx={{ ...getTextStyle('body2'), color: DESIGN_TOKENS.colors.text.secondary }}>
            Liters Processed
          </Typography>
        </Box>
      </Grid>
      
      <Grid size={{ xs: 6, md: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ ...getTextStyle('h3'), color: DESIGN_TOKENS.colors.status.success[500] }}>
            +15%
          </Typography>
          <Typography sx={{ ...getTextStyle('body2'), color: DESIGN_TOKENS.colors.text.secondary }}>
            Efficiency Gain
          </Typography>
        </Box>
      </Grid>
      
      <Grid size={{ xs: 6, md: 3 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ ...getTextStyle('h3'), color: DESIGN_TOKENS.colors.brand.charcoal }}>
            847
          </Typography>
          <Typography sx={{ ...getTextStyle('body2'), color: DESIGN_TOKENS.colors.text.secondary }}>
            Active Farms
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Paper>
);

type ColorPaletteProps = {
  title: string;
  colors: Record<string, string>;
};

const ColorPalette: React.FC<ColorPaletteProps> = ({ title, colors }) => (
  <Box>
    <Typography sx={{ ...getTextStyle('h6'), color: DESIGN_TOKENS.colors.text.secondary, mb: 2 }}>
      {title}
    </Typography>
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {Object.entries(colors).map(([name, hex]) => (
        <Grid key={name} size="auto">
          <ColorSwatch color={hex} name={name} hex={hex} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

export const BrandIdentityShowcase: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 6, backgroundColor: DESIGN_TOKENS.colors.background.default }}>
      <Stack spacing={6}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={{ ...getTextStyle('h3'), color: DESIGN_TOKENS.colors.text.primary, mb: 2 }}>
            MUU Agricultural Essence Design System
          </Typography>
          <Typography sx={{ ...getTextStyle('body1'), color: DESIGN_TOKENS.colors.text.secondary, maxWidth: '600px', mx: 'auto' }}>
            A nature-inspired, minimalist aesthetic. Green tones, grey accents, and a clean white foundation reflect our commitment to sustainable and intelligent agrotech.
          </Typography>
        </Box>

        {/* Color Palette */}
        <Box>
          <Typography sx={{ ...getTextStyle('h5'), mb: 3 }}>
            Color System
          </Typography>
          <ColorPalette title="Brand Colors" colors={DESIGN_TOKENS.colors.brand} />
          <ColorPalette title="Primary Scale" colors={DESIGN_TOKENS.colors.primary} />
          <ColorPalette title="Secondary Scale" colors={DESIGN_TOKENS.colors.secondary} />
          <ColorPalette title="Neutral Scale" colors={DESIGN_TOKENS.colors.neutral} />
        </Box>

        <Divider />

        {/* Typography */}
        <Box>
          <Typography sx={{ ...getTextStyle('h5'), mb: 3 }}>
            Typography System
          </Typography>
          <TypographyShowcase />
        </Box>

        <Divider />

        {/* Component Examples */}
        <Box>
          <Typography sx={{ ...getTextStyle('h5'), mb: 3 }}>
            Component Applications
          </Typography>
          <ComponentShowcase />
        </Box>

        <Divider />

        {/* Data Visualization */}
        <Box>
          <Typography sx={{ ...getTextStyle('h5'), mb: 3 }}>
            Data Presentation
          </Typography>
          <DataVisualization />
        </Box>

        {/* Brand Principles */}
        <Paper sx={{ p: 4, backgroundColor: DESIGN_TOKENS.colors.brand.forest, color: DESIGN_TOKENS.colors.text.inverse }}>
          <Typography sx={{ ...getTextStyle('h5'), mb: 3, color: 'inherit' }}>
            Brand Principles
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 48, mb: 2, color: DESIGN_TOKENS.colors.primary[400] }}>🔬</Box>
                <Typography sx={{ ...getTextStyle('h6'), mb: 1 }}>
                  Innovation
                </Typography>
                <Typography sx={{ ...getTextStyle('body2'), opacity: 0.9 }}>
                  Cutting-edge AI technology for agricultural advancement
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 48, mb: 2, color: DESIGN_TOKENS.colors.secondary[300] }}>☁️</Box>
                <Typography sx={{ ...getTextStyle('h6'), mb: 1 }}>
                  Reliability
                </Typography>
                <Typography sx={{ ...getTextStyle('body2'), opacity: 0.9 }}>
                  Dependable solutions farmers can trust
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 48, mb: 2, color: DESIGN_TOKENS.colors.status.success[500] }}>🌱</Box>
                <Typography sx={{ ...getTextStyle('h6'), mb: 1 }}>
                  Sustainability
                </Typography>
                <Typography sx={{ ...getTextStyle('body2'), opacity: 0.9 }}>
                  Environmental stewardship through smart technology
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    </Container>
  );
};

export default BrandIdentityShowcase;
